import React, { useCallback, useEffect } from "react";
import { Alert, Card, Radio, RadioChangeEvent } from "antd";
import { useHistory } from "react-router";
import ShopType from "@/enum/ShopType";
import getAuthShopUrl, { AppKeyLoginPrefix } from "@/services/getAuthShopUrl";
import { useBoolean } from "ahooks";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import ServicesContactLine from "@/components/ServicesContactLine";
import { appKeyLoginShopTypes } from "@/utils/ShopUtils";
import { useModalStore } from "@/hooks/Modal/hooks";
import ChanelShopLoginModal from "../dialog/ChanelShopLoginModal";
import { observer } from "mobx-react-lite";
import { wechatLoginUrl } from "@/services/getWechatLoginUrl";

interface SelectLoginMethodProps {}

enum LoginMethod {
    MAIN = "MAIN",

    WECHAT = "WECHAT",

    /** 爱库存V2接口登录方式 */
    AIKUCUN_V2 = "AIKUCUN_V2",
}

const AppKeyLoginLabelMap = {
    [ShopType.WEBUY]: "鲸灵、好衣库店铺Id登录",
    [ShopType.HAIZIWANG]: "孩子王登录名、AppKey登录",
    [ShopType.AIKUCUN]: "爱库存APPID登录",
    [ShopType.DOUBB]: "播城登录",
};

/**
 * 选择登录模式页面
 */
const SelectLoginMethod: React.FC<SelectLoginMethodProps> = function (props) {
    const [isShow, { setTrue }] = useBoolean(false);
    const history = useHistory();

    const search = new URLSearchParams(history.location.search);

    const shopType = search.get("shopType") as ShopType;

    const modalStore = useModalStore();

    const shopTypeName = ShopTypeNameMap[shopType];

    useEffect(() => {
        if (!shopType) {
            history.replace("/login");
        }
    }, [shopType]);

    function buildTitle() {
        if (shopType === ShopType.WEBUY) {
            return <span>鲸灵、好衣库用户登录 - 发货易</span>;
        }

        if ([ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(shopType)) {
            return <span>微信小店供货商(代发、供货)用户登录 - 发货易</span>;
        }

        return <span>{shopTypeName}用户登录 - 发货易</span>;
    }

    const isAppKeyLogin = appKeyLoginShopTypes.includes(shopType);

    const onClick = useCallback(async (e: RadioChangeEvent) => {
        const value: LoginMethod = e.target.value;

        switch (value) {
            case LoginMethod.MAIN:
                if (
                    [ShopType.CHANNEL_SHOP, ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(
                        shopType,
                    )
                ) {
                    modalStore.openModal(ChanelShopLoginModal, {
                        shopType,
                    });

                    return;
                }

                const url = getAuthShopUrl(shopType);

                if (url.startsWith(AppKeyLoginPrefix)) {
                    history.push(url);
                } else {
                    window.open(url, "_self");
                }

                break;
            case LoginMethod.WECHAT:
                setTrue();
                break;

            case LoginMethod.AIKUCUN_V2:
                history.push(getAuthShopUrl(ShopType.AIKUCUN_V2));
                break;
            default:
                break;
        }
    }, []);

    function buildMainLoginText() {
        if (isAppKeyLogin) {
            return AppKeyLoginLabelMap[shopType];
        } else {
            if ([ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(shopType)) {
                return "微信小店供货商(代发、供货)登录";
            }

            return shopTypeName + "店铺主账号登录";
        }
    }

    return (
        <div className="ec-select-login-method-page">
            <Card
                title={buildTitle()}
                className="select-login-method-card"
                style={shopType === ShopType.AIKUCUN ? { width: "690px" } : undefined}
            >
                <div className="mb30">
                    <span>选择登录方式：</span>

                    <Radio.Group buttonStyle="solid" value={null} onChange={onClick}>
                        <Radio.Button value={LoginMethod.MAIN}>{buildMainLoginText()}</Radio.Button>

                        {shopType === ShopType.AIKUCUN ? (
                            <Radio.Button value={LoginMethod.AIKUCUN_V2}>
                                <span>
                                    爱库存APPID登录<span className="danger fb">(新接口)</span>
                                </span>
                            </Radio.Button>
                        ) : null}

                        <Radio.Button value={LoginMethod.WECHAT}>微信子账号扫码登录</Radio.Button>
                    </Radio.Group>
                </div>

                {shopType === ShopType.DOUDIAN ? (
                    <span className="small text-gray">
                        抖店店铺子账号请
                        <a target="_blank" href="https://fuwu.jinritemai.com/myService?loginType=1">
                            点击这里
                        </a>
                        从 抖店服务市场—我的服务 登录发货易
                    </span>
                ) : null}

                {isShow ? (
                    <div>
                        <div id="wechat-login" className="mb10">
                            <iframe
                                src={wechatLoginUrl}
                                scrolling="no"
                                width="300px"
                                height="400px"
                                style={{ border: 0 }}
                                sandbox="allow-scripts allow-popups allow-top-navigation allow-top-navigation-by-user-activation allow-same-origin"
                            ></iframe>
                        </div>
                        <div className="mb10">
                            <ServicesContactLine />
                        </div>
                    </div>
                ) : null}

                <Alert
                    showIcon
                    type="info"
                    title={
                        <div>
                            <p>温馨提示：如果您是第一次使用请不要点击“微信子账号扫码登录”</p>
                            <p>微信子账号扫码登录需要先登录进入发货易添加微信子账号才能使用</p>
                            <p>
                                <a href="https://qw15p3s8hv.feishu.cn/wiki/wikcnEEDmxiA5HTcjcY2MNsuV8f" target="_blank">
                                    具体步骤请点击查看教程
                                </a>
                            </p>
                        </div>
                    }
                />
            </Card>
        </div>
    );
};

export default observer(SelectLoginMethod);
